/**
 * First-class dev diagnostics overlay. Off by default on every environment;
 * opt in with `?debug=1` in the URL or Ctrl+Shift+D. When disabled, all
 * record* functions are cheap no-ops.
 *
 * Currently surfaces:
 *   - WS snapshot inter-arrival jitter (last/p50/p95/p99/max in ms)
 *   - Forward-extrapolation prediction error per unit (cells)
 *   - Frame render time (ms)
 *   - Live FPS
 *   - Latest server tick + env label
 *
 * Designed to grow: add a new metric by adding a record* function and a
 * line in renderOverlay(). RingBuffer keeps it bounded.
 */

const RING_SIZE = 100;
const HUD_UPDATE_INTERVAL_MS = 200; // 5 Hz — enough for human reading, cheap

class RingBuffer {
  private readonly buf: number[] = [];
  private readonly cap: number;
  constructor(cap: number) { this.cap = cap; }
  push(v: number): void {
    if (this.buf.length >= this.cap) this.buf.shift();
    this.buf.push(v);
  }
  get last(): number { return this.buf[this.buf.length - 1] ?? 0; }
  get count(): number { return this.buf.length; }
  get values(): readonly number[] { return this.buf; }
  /** Returns p50/p95/p99/max. Allocates a sorted copy each call — cheap at N=100. */
  percentiles(): { p50: number; p95: number; p99: number; max: number } {
    if (this.buf.length === 0) return { p50: 0, p95: 0, p99: 0, max: 0 };
    const sorted = [...this.buf].sort((a, b) => a - b);
    const at = (frac: number): number => sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * frac))]!;
    return { p50: at(0.5), p95: at(0.95), p99: at(0.99), max: sorted[sorted.length - 1]! };
  }
}

let enabled = false;
let visible = false;
let mounted = false;

const arrivalDeltasMs = new RingBuffer(RING_SIZE);
const frameTimesMs = new RingBuffer(RING_SIZE);
const predictionErrorCells = new RingBuffer(RING_SIZE);
const rafIntervalMs = new RingBuffer(RING_SIZE);

let lastArrivalMs = 0;
let lastRafMs = 0;
let latestServerTick = -1;
let envLabel = "?";
let activeMatches = "?";

let overlayEl: HTMLDivElement | null = null;
let updateTimer: number | null = null;

function isForceEnabled(): boolean {
  return new URLSearchParams(location.search).get("debug") === "1";
}

/** Idempotent. Call once on app start. */
export function init(): void {
  if (mounted) return;
  enabled = isForceEnabled();
  if (!enabled) {
    // Bind the keyboard toggle so the user can opt in mid-session by pressing
    // Ctrl+Shift+D (turns on enabled+visible together).
    bindKeyboardToggle();
    return;
  }
  visible = true;
  mountOverlay();
  bindKeyboardToggle();
  startUpdateLoop();
  mounted = true;
}

function bindKeyboardToggle(): void {
  window.addEventListener("keydown", (e) => {
    if (e.ctrlKey && e.shiftKey && (e.key === "D" || e.key === "d")) {
      e.preventDefault();
      if (!enabled) {
        enabled = true;
        visible = true;
        mountOverlay();
        startUpdateLoop();
        mounted = true;
      } else {
        visible = !visible;
        if (overlayEl) overlayEl.style.display = visible ? "block" : "none";
      }
    }
  });
}

function mountOverlay(): void {
  if (overlayEl !== null) return;
  overlayEl = document.createElement("div");
  overlayEl.id = "debug-overlay";
  overlayEl.style.cssText = [
    "position:fixed",
    "top:8px",
    "right:8px",
    "z-index:99999",
    "background:rgba(10,12,18,0.85)",
    "color:#cce",
    "font:11px/1.35 ui-monospace,Menlo,Monaco,Consolas,monospace",
    "padding:8px 10px",
    "border:1px solid rgba(120,140,200,0.35)",
    "border-radius:4px",
    "min-width:220px",
    "pointer-events:none",
    "white-space:pre",
  ].join(";");
  overlayEl.style.display = visible ? "block" : "none";
  document.body.appendChild(overlayEl);
}

function startUpdateLoop(): void {
  if (updateTimer !== null) return;
  updateTimer = window.setInterval(renderOverlay, HUD_UPDATE_INTERVAL_MS);
}

function fmt(n: number, digits = 1): string {
  return n.toFixed(digits);
}

function renderOverlay(): void {
  if (overlayEl === null || !visible) return;

  const arr = arrivalDeltasMs.percentiles();
  const frame = frameTimesMs.percentiles();
  const pred = predictionErrorCells.percentiles();
  const raf = rafIntervalMs.percentiles();

  const fps = raf.p50 > 0 ? Math.round(1000 / raf.p50) : 0;

  // Color the snap arrival p99 if it's wildly off 100ms — that's the smoking
  // gun for "snapshots are arriving with jitter the buffer would have to absorb".
  const arrJitter = Math.max(0, arr.p99 - 100);
  const arrColor = arrJitter > 30 ? "#ff7a7a" : arrJitter > 10 ? "#ffd060" : "#a0d8ff";

  const predColor = pred.p95 > 0.3 ? "#ff7a7a" : pred.p95 > 0.1 ? "#ffd060" : "#a0d8ff";

  overlayEl.innerHTML = [
    `<b style="color:#fff">towerhold debug</b> <span style="color:#888">[Ctrl+Shift+D]</span>`,
    ``,
    `<b>ws snap arrival</b>  <span style="color:${arrColor}">jitter +${fmt(arrJitter)}ms</span>`,
    `  last  ${fmt(arrivalDeltasMs.last)} ms`,
    `  p50   ${fmt(arr.p50)} ms`,
    `  p95   ${fmt(arr.p95)} ms`,
    `  p99   ${fmt(arr.p99)} ms`,
    `  max   ${fmt(arr.max)} ms`,
    `  N=${arrivalDeltasMs.count}`,
    ``,
    `<b>predict error</b>  <span style="color:${predColor}">p95 ${fmt(pred.p95, 2)} cells</span>`,
    `  last  ${fmt(predictionErrorCells.last, 3)} cells`,
    `  p50   ${fmt(pred.p50, 3)} cells`,
    `  p95   ${fmt(pred.p95, 3)} cells`,
    `  max   ${fmt(pred.max, 3)} cells`,
    ``,
    `<b>render frame</b>`,
    `  p50   ${fmt(frame.p50)} ms`,
    `  p95   ${fmt(frame.p95)} ms`,
    `  fps   ${fps}`,
    ``,
    `<b>match</b>`,
    `  tick  ${latestServerTick >= 0 ? latestServerTick : "—"}`,
    `  env   ${envLabel}`,
  ].join("\n");
}

// ---------------------------------------------------------------------------
// Public record API. All cheap no-ops when disabled.
// ---------------------------------------------------------------------------

export function recordSnapshotArrival(tick: number, nowMs: number): void {
  if (!enabled) return;
  if (lastArrivalMs > 0) {
    arrivalDeltasMs.push(nowMs - lastArrivalMs);
  }
  lastArrivalMs = nowMs;
  if (tick > latestServerTick) latestServerTick = tick;
}

export function recordFrameTime(durationMs: number, nowMs: number): void {
  if (!enabled) return;
  frameTimesMs.push(durationMs);
  if (lastRafMs > 0) {
    rafIntervalMs.push(nowMs - lastRafMs);
  }
  lastRafMs = nowMs;
}

export function recordPredictionError(avgErrorCells: number): void {
  if (!enabled) return;
  predictionErrorCells.push(avgErrorCells);
}

export function setEnvLabel(env: string): void {
  envLabel = env;
}

export function setActiveMatches(s: string): void {
  activeMatches = s;
}

/** True if the overlay is in any state (visible or hidden, but instrumented). */
export function isEnabled(): boolean {
  return enabled;
}
